import { SyntheticEvent, useEffect, useRef } from 'react';
import { TFilterType } from '@legacy/apis/types/filterListAPI';
import { Autocomplete } from '@legacy/components/mui-components';
import { DataAutomationId } from '@legacy/utils/ts-utils';

export interface IFilterSelectOnChangeEvent {
  label?: string;
  value: string;
}

export interface IFilterItemProperties<U>
  extends Partial<Omit<HTMLFormElement, 'contentEditable'>>,
    DataAutomationId {
  defaultValue?: U;
  fieldLabel: string;
  fetchNextPage?: () => void;
  filterId: string;
  hiddenLabel?: boolean;
  options?: Array<IFilterSelectOnChangeEvent>;
  type?: TFilterType;
  latestPageChildrenLength?: number;
  take?: number;
}

export type TFilterItemProperties = IFilterItemProperties<
  Array<IFilterSelectOnChangeEvent>
>;

const FilterInput = ({
  addFilterItem,
  defaultValue,
  fetchNextPage,
  fieldLabel,
  filterId,
  options = [],
  type,
  latestPageChildrenLength,
  take,
  ...restProps
}: TFilterItemProperties) => {
  const inputAutoCompleteRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputAutoCompleteRef.current) {
      inputAutoCompleteRef.current.focus();
    }
  }, [options]);

  return (
    <Autocomplete
      componentsProps={{
        popper: {
          modifiers: [
            {
              // We add this to prevent the popper from hiding the "is" and "is not" radio buttons
              name: 'flip',
              enabled: false,
            },
          ],
        },
      }}
      defaultValue={defaultValue}
      disableClearable
      disableCloseOnSelect
      disablePortal
      fullWidth
      inputRef={inputAutoCompleteRef}
      ListboxProps={{
        sx: { maxHeight: '40vh' },
        onScroll: (event: SyntheticEvent) => {
          const listboxNode = event.currentTarget;
          const scrollTop = Math.round(listboxNode.scrollTop);
          const clientHeight = Math.round(listboxNode.clientHeight);
          const scrollHeight = Math.round(listboxNode.scrollHeight);
          const threshold = 2; // pixels of threshold to account for rounding errors

          // Check if we're close enough to the bottom (within threshold)
          if (scrollTop + clientHeight + threshold < scrollHeight) {
            return;
          }
          if (take === latestPageChildrenLength) fetchNextPage?.();
        },
      }}
      multiple={type === 'MultiSelectTypeSearch'}
      options={options}
      {...restProps}
      data-automation-id={restProps['data-automation-id']}
    />
  );
};

export default FilterInput;
